import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import CpdTextField from '@/components/ui/CpdTextField';

const ClientBankAccountBasicInfoSection = forwardRef(
  ({ onFormChange = () => {}, fieldErrors = {} }, ref) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
      display_name: '',
      business_payee_name: '',
      account_number: '',
      routing_number: '',
      email: '',
      phone: '',
      provider: 'moov',
      check_template: '',
    });

    useEffect(() => {
      onFormChange(formData);
    }, [formData, onFormChange]);

    const handleFormChange = (field, value) => {
      setFormData((prev) => {
        const next = Object.assign({}, prev, { [field]: value });
        if (field === 'provider') {
          next.check_template = '';
        }
        return next;
      });
    };

    const templateTooLong =
      formData.provider === 'self_hosted' &&
      Array.from(formData.check_template.trim()).length > 35;
    let templateError = fieldErrors.check_template;
    if (templateTooLong) {
      templateError = t('clientBankAccounts.validation.checkTemplateLength');
    }

    useImperativeHandle(ref, () => ({
      getFormData: () => {
        if (templateTooLong) {
          return null;
        }
        const payload = Object.assign({}, formData);
        delete payload.check_template;
        if (formData.provider === 'self_hosted') {
          payload.check_template = formData.check_template.trim() || null;
        }
        return payload;
      },
    }));

    return (
      <Box>
        <Typography sx={{ mb: 1.5 }} variant='h6' gutterBottom>
          Bank Account Information
        </Typography>

        <FormControl variant='outlined' size='small' fullWidth sx={{ mb: 2 }}>
          <InputLabel id='bank-account-provider'>Provider</InputLabel>
          <Select
            labelId='bank-account-provider'
            label='Provider'
            value={formData.provider}
            onChange={(e) => handleFormChange('provider', e.target.value)}>
            <MenuItem value='moov'>Moov</MenuItem>
            <MenuItem value='self_hosted'>Self Hosted</MenuItem>
          </Select>
        </FormControl>

        <CpdTextField
          label={t('clientBankAccounts.form.displayName')}
          value={formData.display_name}
          onChange={(e) => handleFormChange('display_name', e.target.value)}
          required
          autoComplete='organization'
          error={!!fieldErrors.display_name}
          helperText={fieldErrors.display_name}
        />

        <CpdTextField
          label={t('clientBankAccounts.form.businessPayeeName')}
          value={formData.business_payee_name}
          onChange={(e) =>
            handleFormChange('business_payee_name', e.target.value)
          }
          inputProps={{ maxLength: 60 }}
          required
          autoComplete='name'
          error={!!fieldErrors.business_payee_name}
          helperText={fieldErrors.business_payee_name}
        />

        <CpdTextField
          label={t('clientBankAccounts.form.accountNumber')}
          value={formData.account_number}
          onChange={(e) => handleFormChange('account_number', e.target.value)}
          required
          autoComplete='off'
          error={!!fieldErrors.account_number}
          helperText={fieldErrors.account_number}
        />

        <CpdTextField
          label={t('clientBankAccounts.form.routingNumber')}
          value={formData.routing_number}
          onChange={(e) => handleFormChange('routing_number', e.target.value)}
          required
          autoComplete='off'
          error={!!fieldErrors.routing_number}
          helperText={fieldErrors.routing_number}
        />

        {formData.provider === 'self_hosted' && (
          <FormControl variant='outlined' size='small' fullWidth sx={{ mb: 2 }}>
            <InputLabel id='bank-name'>Bank Name</InputLabel>
            <Select
              labelId='bank-name'
              label='Bank Name'
              value='wells_fargo'
              disabled>
              <MenuItem value='wells_fargo'>Wells Fargo</MenuItem>
            </Select>
          </FormControl>
        )}

        {formData.provider === 'self_hosted' && (
          <CpdTextField
            label={t('clientBankAccounts.form.checkTemplate')}
            value={formData.check_template}
            onChange={(e) => handleFormChange('check_template', e.target.value)}
            autoComplete='off'
            error={!!templateError}
            helperText={templateError}
          />
        )}

        {formData.provider === 'moov' && (
          <>
            <Divider sx={{ mb: 3, mt: 3 }} />

            <Typography variant='h6' gutterBottom>
              Contact Info
            </Typography>

            <Typography
              sx={{ mb: 1.5, mt: 1 }}
              variant='body2'
              color='text.secondary'>
              Either Email or Phone are mandatory
            </Typography>

            <CpdTextField
              label={t('clientBankAccounts.form.email')}
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              autoComplete='email'
              type='email'
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />

            <CpdTextField
              label={t('clientBankAccounts.form.phone')}
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              autoComplete='tel'
              type='tel'
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
            />
          </>
        )}
      </Box>
    );
  },
);

ClientBankAccountBasicInfoSection.displayName =
  'ClientBankAccountBasicInfoSection';

export default ClientBankAccountBasicInfoSection;
